import { ofType, combineEpics } from "redux-observable";
import * as Rx from "rxjs";
import * as RxOp from "rxjs/operators";
import * as S from "sanctuary";
import * as $ from "sanctuary-def";

import { getCurrentUser, editMyself } from "@/APIClient";
import { requestEpic } from "@/APIClient/observable.util";
import { fetchAuthorizedData } from "@/app-config-epic";
import {
  addAlert,
  clearAlerts,
  replaceAlerts,
} from "@/components/alert-bar/AlertBar.state";
import { requestClients } from "@/components/adminPage/AdminPage.state";
import { requestAgencies } from "@/components/currentUser/Agencies.state";
import { getAuthToken, logOut } from "@/util/session";
import { setLoadingOverlayIsActive } from "@/state/shared.state";
import { editProfileAlertBarAction } from "../profilePage/ProfilePage.state";
import {
  REQUEST_CURRENT_USER,
  EDIT_MYSELF,
  setCurrentUserError,
  setCurrentUser,
  editMyselfFailed,
  editMyselfSucceeded,
  requestCurrentUserStarted,
  REQUEST_CLIENT,
} from "./CurrentUser.state";
import {
  loginError,
  loginAlertBarAction,
} from "@/components/loginPage/LoginPage.state";

const createDeactivationErr = (email) =>
  S.Just({
    title: "User deactivated",
    message: `The user ${email} has been deactivated. Please contact your administrator with any questions.`,
  });

const onFailure = (oktaAuth) => async (error) =>
  Rx.of(
    await logOut(oktaAuth),
    setCurrentUserError({ error }),
    loginError(error),
    loginAlertBarAction(
      replaceAlerts({
        heading: "Authentication Failed",
        text: "We were unable to retrieve your account information. Please try again.",
        variant: "error",
      })
    )
  );

const requestCurrentUserEpic = (action$) =>
  action$.pipe(
    ofType(REQUEST_CURRENT_USER),
    RxOp.mergeMap(({ payload: { accessToken, oktaAuth } }) => {
      // If there is an accessToken, this is an Okta user.
      const token = accessToken || S.fromMaybe("")(getAuthToken());

      return Rx.concat([
        Rx.of(requestCurrentUserStarted()),
        requestEpic({
          requestMethod: getCurrentUser,
          requestArgs: {
            token,
          },
          onSuccess: (res) =>
            S.pipe([S.map(S.props(["isActive", "value"])), S.fromEither(false)])(res)
              ? Rx.of(setCurrentUser({ user: res.value }), fetchAuthorizedData())
              : onFailure(oktaAuth)(
                  createDeactivationErr(
                    S.fromMaybe("")(
                      S.gets(S.is($.String))(["value", "email", "value"])(res)
                    )
                  )
                ),
          onFailure: onFailure(oktaAuth),
        }),
      ]);
    }),
    RxOp.concatAll(),
    RxOp.catchError((e) => setCurrentUserError({ error: e }))
  );

const editMyselfEpic = (action$, state$) =>
  action$.pipe(
    ofType(EDIT_MYSELF),
    RxOp.mergeMap(({ payload: { firstName, lastName, timezone, oktaAuth } }) =>
      Rx.concat(
        Rx.of(setLoadingOverlayIsActive(true)),
        requestEpic({
          requestMethod: editMyself,
          requestArgs: {
            firstName,
            lastName,
            timezone,
            token: S.fromMaybe("")(getAuthToken()),
            userId: state$.value.currentUser?.id?.value,
          },
          onSuccess: (res) => {
            // We need to renew the access token so it has the updated profile information
            // (i.e. first_name, last_name) in the claims.
            // This is necessary because the /myself endpoint just decodes the access token,
            // and returns the first_name and last_name present in the claims.
            oktaAuth.tokenManager.renew("accessToken");

            return Rx.of(
              setLoadingOverlayIsActive(false),
              editMyselfSucceeded({ profile: res.value.profile.value }),
              editProfileAlertBarAction(clearAlerts()),
              editProfileAlertBarAction(
                addAlert({
                  heading: "Profile Update Successful",
                  text: "Your profile has been updated successfully",
                  variant: "success",
                })
              )
            );
          },
          onFailure: (err) =>
            Rx.of(
              setLoadingOverlayIsActive(false),
              editMyselfFailed({ error: err.value }),
              editProfileAlertBarAction(clearAlerts()),
              editProfileAlertBarAction(
                addAlert({
                  heading: "Profile Update Failed",
                  text: "An error ocurred while trying to update your profile. Please try again later.",
                  variant: "error",
                })
              )
            ),
        })
      )
    )
  );

const requestClientAsAdminUserEpic = (action$) =>
  action$.pipe(
    ofType(REQUEST_CLIENT),
    RxOp.mergeMap(({ payload: { client, oktaAuth } }) =>
      requestEpic({
        requestMethod: getCurrentUser,
        requestArgs: {
          client,
          token: S.fromMaybe("")(getAuthToken()),
        },
        onSuccess: (res) =>
          S.pipe([S.map(S.props(["isActive", "value"])), S.fromEither(false)])(res)
            ? Rx.of(
                setCurrentUser({ user: res.value }),
                fetchAuthorizedData(),
                requestAgencies(),
                requestClients()
              )
            : onFailure(oktaAuth)(
                createDeactivationErr(
                  S.fromMaybe("")(
                    S.gets(S.is($.String))(["value", "email", "value"])(res)
                  )
                )
              ),
        onFailure: onFailure(oktaAuth),
      })
    )
  );

const epic = combineEpics(
  requestCurrentUserEpic,
  editMyselfEpic,
  requestClientAsAdminUserEpic
);
export default epic;
